import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  Input,
  Switch,
  useToast,
} from '@ais-ui/ui-core';
import * as z from 'zod';
import { useSettings } from '../SettingsProvider';
import { useSaveSettings } from '@ais-ui/websockets';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@ais-ui/utils';
import React, { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { FormInput } from 'lucide-react';
import { useSettingsPage } from '../SettingsPageProvider';

const labelFormSchema = z.object({
  labelZoomLevel: z.number(),
  labelZoomLevelFilter: z.number(),
  name: z.boolean(),
  mmsi: z.boolean(),
  callSign: z.boolean(),
  imo: z.boolean(),
  typeOfShip: z.boolean(),
  typeOfCargo: z.boolean(),
  country: z.boolean(),
  destination: z.boolean(),
  size: z.boolean(),
  speedOverGround: z.boolean(),
  courseOverGround: z.boolean(),
  heading: z.boolean(),
  draught: z.boolean(),
  navigationalStatus: z.boolean(),
  aisType: z.boolean(),
});

type LabelFormValues = z.infer<typeof labelFormSchema>;

export const LabelForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    labelZoomLevel: settings['ribbonPlugin.settings'].label_zoom_level_nofilter
      ? settings['ribbonPlugin.settings'].label_zoom_level_nofilter
      : 9,
    labelZoomLevelFilter: settings['ribbonPlugin.settings']
      .label_zoom_level_filter
      ? settings['ribbonPlugin.settings'].label_zoom_level_filter
      : 9,
    name: settings['ribbonPlugin.settings'].show_label_name
      ? settings['ribbonPlugin.settings'].show_label_name
      : false,
    mmsi: settings['ribbonPlugin.settings'].show_label_mmsi
      ? settings['ribbonPlugin.settings'].show_label_mmsi
      : false,
    aisType: settings['ribbonPlugin.settings'].show_label_ais_type
      ? settings['ribbonPlugin.settings'].show_label_ais_type
      : false,
    callSign: settings['ribbonPlugin.settings'].show_label_callsign
      ? settings['ribbonPlugin.settings'].show_label_callsign
      : false,
    imo: settings['ribbonPlugin.settings'].show_label_imo
      ? settings['ribbonPlugin.settings'].show_label_imo
      : false,
    typeOfShip: settings['ribbonPlugin.settings'].show_label_shiptype
      ? settings['ribbonPlugin.settings'].show_label_shiptype
      : false,
    typeOfCargo: settings['ribbonPlugin.settings'].show_label_cargotype
      ? settings['ribbonPlugin.settings'].show_label_cargotype
      : false,
    country: settings['ribbonPlugin.settings'].show_label_country
      ? settings['ribbonPlugin.settings'].show_label_country
      : false,
    destination: settings['ribbonPlugin.settings'].show_label_destination
      ? settings['ribbonPlugin.settings'].show_label_destination
      : false,
    size: settings['ribbonPlugin.settings'].show_label_size
      ? settings['ribbonPlugin.settings'].show_label_size
      : false,
    speedOverGround: settings['ribbonPlugin.settings'].show_label_sog
      ? settings['ribbonPlugin.settings'].show_label_sog
      : false,
    courseOverGround: settings['ribbonPlugin.settings'].show_label_cog
      ? settings['ribbonPlugin.settings'].show_label_cog
      : false,
    heading: settings['ribbonPlugin.settings'].show_label_heading
      ? settings['ribbonPlugin.settings'].show_label_heading
      : false,
    draught: settings['ribbonPlugin.settings'].show_label_draught
      ? settings['ribbonPlugin.settings'].show_label_draught
      : false,
    navigationalStatus: settings['ribbonPlugin.settings'].show_label_navstatus
      ? settings['ribbonPlugin.settings'].show_label_navstatus
      : false,
  };

  const form = useForm<LabelFormValues>({
    resolver: zodResolver(labelFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: LabelFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        label_zoom_level_nofilter: data.labelZoomLevel,
        label_zoom_level_filter: data.labelZoomLevelFilter,
        show_label_name: data.name,
        show_label_mmsi: data.mmsi,
        show_label_callsign: data.callSign,
        show_label_imo: data.imo,
        show_label_shiptype: data.typeOfShip,
        show_label_cargotype: data.typeOfCargo,
        show_label_country: data.country,
        show_label_destination: data.destination,
        show_label_size: data.size,
        show_label_sog: data.speedOverGround,
        show_label_cog: data.courseOverGround,
        show_label_heading: data.heading,
        show_label_draught: data.draught,
        show_label_navstatus: data.navigationalStatus,
        show_label_ais_type: data.aisType,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-label-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsMapLabelSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-label-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsMapLabelFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div>
          <div className="space-y-4">
            <h3 className="text-lg font-medium">
              {messages.settingsMapLabelTitle}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                key="labelZoomLevel"
                control={form.control}
                name="labelZoomLevel"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelZoomLevelLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelZoomLevelDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="zoom"
                        className="w-20"
                        min={0}
                        max={22}
                        {...field}
                        onChange={(e) => {
                          if (!e.target.value) {
                            field.onChange('');
                          } else {
                            // Convert the string value to a number
                            let numberValue = parseInt(e.target.value);

                            // Enforce min/max bounds
                            if (numberValue < 0) numberValue = 0;
                            if (numberValue > 22) numberValue = 22;
                            field.onChange(numberValue);
                          }
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelZoomLevelFilter"
                control={form.control}
                name="labelZoomLevelFilter"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelZoomLevelFilterLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelZoomLevelFilterDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="zoom"
                          min={0}
                          max={22}
                          className="w-20"
                          {...field}
                          onChange={(e) => {
                            if (!e.target.value) {
                              field.onChange('');
                            } else {
                              // Convert the string value to a number
                              let numberValue = parseInt(e.target.value);

                              // Enforce min/max bounds
                              if (numberValue < 0) numberValue = 0;
                              if (numberValue > 22) numberValue = 22;
                              field.onChange(numberValue);
                            }
                          }}
                        />
                      </FormControl>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelName"
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelNameLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelNameDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-name"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelMmsi"
                control={form.control}
                name="mmsi"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-lg p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelMmsiLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelMmsiDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-mmsi"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelAisType"
                control={form.control}
                name="aisType"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelAisTypeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelAisTypeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-aisType"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelCallSign"
                control={form.control}
                name="callSign"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelCallsignLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelCallsignDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-callSign"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelImo"
                control={form.control}
                name="imo"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelImoLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelImoDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-imo"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelTypeOfShip"
                control={form.control}
                name="typeOfShip"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelTypeOfShipLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelTypeOfShipDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-typeOfShip"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelTypeOfCargo"
                control={form.control}
                name="typeOfCargo"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelTypeOfCargoLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelTypeOfCargoDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-typeOfCargo"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelCountry"
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelCountryLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelCountryDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-country"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelDestination"
                control={form.control}
                name="destination"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelDestinationLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelDestinationDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-destination"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="lableSize"
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelSizeLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelSizeDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-size"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelSpeedOverGround"
                control={form.control}
                name="speedOverGround"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelSpeedOverGroundLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelSpeedOverGroundDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-speedOverGround"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelCourseOverGround"
                control={form.control}
                name="courseOverGround"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelCourseOverGroundLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelCourseOverGroundDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-courseOverGround"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelHeading"
                control={form.control}
                name="heading"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelHeadingLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelHeadingDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-heading"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelDraught"
                control={form.control}
                name="draught"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelDraughtLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelDraughtDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-draught"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                key="labelNavigationalStatus"
                control={form.control}
                name="navigationalStatus"
                render={({ field }) => (
                  <FormItem
                    className={cn(
                      'flex flex-row items-center justify-between border rounded-lg p-4'
                    )}
                  >
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {messages.settingsMapLabelNavigationalStatusLabel}
                      </FormLabel>
                      <FormDescription>
                        {messages.settingsMapLabelNavigationalStatusDescription}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        id="settings-label-navigationalStatus"
                        checked={field.value}
                        onCheckedChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-labels-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
